import React, { useState, useMemo } from 'react';
import { Loader2, RefreshCw, ShoppingCart, Settings, Coins, Trash2, Crown } from 'lucide-react';
import ActionChip from '@components/ui/general/ActionChip/ActionChip';
import UnitsPreview from '@components/ui/general/UnitsPreview';
import CustomTemplatesModal from './CustomTemplatesModal';
import { LOADOUT_CATEGORY_META as CATEGORY_META } from '@/utils/loadoutHelpers';
import { useT } from '@/i18n';
import './LoadoutSelector.css';

/**
 * LoadoutSelector - Loadout selection UI
 *
 * Categories (SSoT from backend):
 * - random: Random units + regenerate button
 * - solo: Single unit type templates
 * - official: Mixed unit combos
 * - custom: Saved custom template
 * - custom_unsaved: Modified roster without saved template
 *
 * Modes:
 * - 'human' (default): Full functionality with custom
 * - 'bot': Only template categories, no custom
 */
export default function LoadoutSelector({
  loadoutTemplates = [],
  activeCategory = 'custom_unsaved',
  appliedLoadoutId = null,
  purchasedUnits = [],
  onApplyLoadout,
  onRegenerateRandom,
  onSwitchToCustom,
  onGoToShop,
  onCreateLoadout,
  onUpdateLoadout,
  onDeleteLoadout,
  isApplying = false,
  isSwitchingToCustom = false,
  isLoading = false,
  isCreatingLoadout = false,
  isUpdatingLoadout = false,
  isDeletingLoadout = false,
  maxCustomTemplates, // Required - no fallback
  disabled = false,
  mode = 'human', // 'human' or 'bot'
  // Editable mode props (for shop view)
  editable = false,
  onRemoveUnit,
  onClearAll,
  unitCount = 0,
  maxUnits, // Required - no fallback
  isRemoving = false,
  money = 0,
}) {
  const t = useT();
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false); // Custom templates modal

  // Group templates by category
  const groupedTemplates = useMemo(() => {
    const grouped = {
      random: [],
      solo: [],
      official: [],
      debug: [],
    };

    loadoutTemplates.forEach((template) => {
      const category = template.category || 'custom';
      if (grouped[category]) {
        grouped[category].push(template);
      }
    });

    return grouped;
  }, [loadoutTemplates]);

  // Filter player-created templates for the custom modal
  const playerTemplates = useMemo(() => {
    return loadoutTemplates.filter((t) => t.visibility === 'player');
  }, [loadoutTemplates]);

  // Get current loadout display name
  const currentLoadoutName = useMemo(() => {
    // custom_unsaved: no template, show "Custom"
    if (activeCategory === 'custom_unsaved') {
      return 'Custom';
    }
    // custom with saved template
    if (activeCategory === 'custom') {
      const template = loadoutTemplates.find((t) => t.id === appliedLoadoutId);
      return template?.name || 'Custom';
    }
    // Other categories: show template name
    const template = loadoutTemplates.find((t) => t.id === appliedLoadoutId);
    return template?.name || null;
  }, [activeCategory, loadoutTemplates, appliedLoadoutId]);

  // Get available categories (random, solo, official + custom for humans, debug for bots)
  const availableCategories = useMemo(() => {
    const categories = [];

    // Add categories that have templates
    if (groupedTemplates.random.length > 0) categories.push('random');
    if (groupedTemplates.solo.length > 0) categories.push('solo');
    if (groupedTemplates.official.length > 0) categories.push('official');

    // Debug only for bot mode
    if (mode === 'bot' && groupedTemplates.debug.length > 0) {
      categories.push('debug');
    }

    // Custom only for human mode
    if (mode === 'human') {
      categories.push('custom');
    }

    return categories;
  }, [groupedTemplates, mode]);

  // Handle category tab click
  const handleCategoryClick = (categoryKey) => {
    const templates = groupedTemplates[categoryKey] || [];

    if (categoryKey === 'custom') {
      // If already in custom/custom_unsaved, toggle dropdown (if player templates exist)
      if (activeCategory === 'custom' || activeCategory === 'custom_unsaved') {
        if (playerTemplates.length > 0) {
          setOpenDropdown(openDropdown === 'custom' ? null : 'custom');
        }
        // Already in custom, nothing more to do
        return;
      }

      // Switching FROM another category TO custom
      // Call switchToCustom to change category without modifying units/money
      onSwitchToCustom?.();
      setOpenDropdown(null);
    } else if (categoryKey === 'random') {
      // Random: apply random template
      // FAIL LOUDLY: Random category must have at least one template
      const randomTemplate = templates[0];
      if (!randomTemplate) {
        throw new Error('[LoadoutSelector] Random category has no templates');
      }
      onApplyLoadout?.(randomTemplate.id);
      setOpenDropdown(null);
    } else if (templates.length === 1) {
      // Single option: apply immediately
      onApplyLoadout?.(templates[0].id);
      setOpenDropdown(null);
    } else if (templates.length > 1) {
      // Multiple options: toggle dropdown
      setOpenDropdown(openDropdown === categoryKey ? null : categoryKey);
    }
  };

  // Handle option select from dropdown
  const handleOptionSelect = (template) => {
    onApplyLoadout?.(template.id);
    setOpenDropdown(null);
  };

  // Handle regenerate click
  const handleRegenerate = (e) => {
    e.stopPropagation();
    onRegenerateRandom?.();
  };

  // Close dropdown when clicking outside
  const handleClickOutside = () => {
    setOpenDropdown(null);
  };

  if (isLoading) {
    return (
      <div className="loadout-selector loadout-selector--loading">
        <Loader2 size={20} className="spinner" />
        <span>{t('loadout.loading_templates')}</span>
      </div>
    );
  }

  return (
    <div className="loadout-selector" onClick={handleClickOutside}>
      {/* Row 1: Template name + Units counter + Money + Trash */}
      {editable && (
        <div className="loadout-header-row">
          <div className="loadout-header-left">
            <span className="loadout-label-name">{currentLoadoutName || t('loadout.no_template')}</span>
          </div>
          <div className="loadout-header-right">
            <div className="loadout-units-badge">
              <Crown size={14} />
              <span>{unitCount}/{maxUnits}</span>
            </div>
            <div className="loadout-money">
              <Coins size={14} />
              <span>${money}</span>
            </div>
            <button
              type="button"
              className="button-mini button-mini-danger"
              onClick={onClearAll}
              disabled={isRemoving || unitCount === 0}
              title={t('loadout.clear_all')}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Non-editable header (original style) */}
      {!editable && (
        <div className="loadout-selector-label">
          <span className="loadout-label-title">{t('loadout.template_label')}</span>
          {currentLoadoutName && (
            <span className="loadout-label-name">{currentLoadoutName}</span>
          )}
        </div>
      )}

      {/* Row 2: Category Tabs */}
      <div className="loadout-tabs-row">
        <div className="loadout-tabs">
        {availableCategories.map((categoryKey) => {
          const meta = CATEGORY_META[categoryKey];
          const IconComponent = meta.icon;
          // Custom tab should be active for both 'custom' and 'custom_unsaved'
          const isActive = categoryKey === 'custom'
            ? (activeCategory === 'custom' || activeCategory === 'custom_unsaved')
            : activeCategory === categoryKey;
          const templates = groupedTemplates[categoryKey] || [];

          // Custom category uses playerTemplates for dropdown
          const dropdownTemplates = categoryKey === 'custom' ? playerTemplates : templates;
          const hasDropdown = categoryKey === 'custom'
            ? playerTemplates.length > 0
            : (templates.length > 1 && categoryKey !== 'random');
          const isDropdownOpen = openDropdown === categoryKey;

          // Find selected template for this category
          const selectedTemplate = dropdownTemplates.find((t) => t.id === appliedLoadoutId);

          return (
            <div key={categoryKey} className="loadout-tab-wrapper">
              <ActionChip
                icon={IconComponent}
                label={meta.label}
                isActive={isActive}
                isLoading={isApplying && isActive}
                disabled={disabled || isApplying}
                hasDropdown={hasDropdown}
                isDropdownOpen={isDropdownOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryClick(categoryKey);
                }}
                title={meta.description}
              >
                {/* Regenerate button for random */}
                {meta.hasRegenerate && isActive && (
                  <ActionChip.Action
                    icon={RefreshCw}
                    onClick={handleRegenerate}
                    disabled={disabled || isApplying}
                    isLoading={isApplying}
                    title={t('loadout.regenerate_random')}
                  />
                )}

                {/* Settings button for custom */}
                {categoryKey === 'custom' && isActive && (
                  <ActionChip.Action
                    icon={Settings}
                    onClick={() => setIsCustomModalOpen(true)}
                    disabled={disabled || isApplying}
                    title={t('loadout.manage_templates')}
                  />
                )}
              </ActionChip>

              {/* Dropdown menu */}
              {hasDropdown && isDropdownOpen && (
                <div className="xc-action-chip__dropdown" onClick={(e) => e.stopPropagation()}>
                  {dropdownTemplates.map((template) => {
                    const isSelected = selectedTemplate?.id === template.id;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        className={`xc-action-chip__option ${isSelected ? 'xc-action-chip__option--selected' : ''}`}
                        onClick={() => handleOptionSelect(template)}
                      >
                        <div className="xc-action-chip__option-content">
                          <span className="xc-action-chip__option-label">{template.name}</span>
                          {template.units && (
                            <span className="xc-action-chip__option-description">
                              {template.units.map((u) => `${u.quantity}x ${u.unit_template_slug}`).join(', ')}
                            </span>
                          )}
                        </div>
                        {isSelected && <span className="xc-action-chip__option-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Units Preview */}
      <div className="loadout-units-row">
        {(() => {
          // Custom mode (custom or custom_unsaved) - show current roster
          if (activeCategory === 'custom' || activeCategory === 'custom_unsaved') {
            return (
              <UnitsPreview
                purchasedUnits={purchasedUnits}
                emptyMessage={t('loadout.no_units_buy')}
                editable={editable}
                onRemoveUnit={onRemoveUnit}
                onClearAll={onClearAll}
                unitCount={unitCount}
                maxUnits={maxUnits}
                isRemoving={isRemoving}
              />
            );
          }

          // Template mode - find the applied template
          const template = loadoutTemplates.find((t) => t.id === appliedLoadoutId);
          if (!template) {
            return (
              <UnitsPreview
                emptyMessage={t('loadout.select_template')}
              />
            );
          }

          // Random template (units is null) - show actual purchased units or loading
          if (!template.units) {
            return (
              <UnitsPreview
                purchasedUnits={purchasedUnits}
                isLoading={!purchasedUnits || purchasedUnits.length === 0}
                loadingMessage={t('loadout.generating_random')}
                emptyMessage={t('loadout.generating_random')}
                editable={editable}
                onRemoveUnit={onRemoveUnit}
                onClearAll={onClearAll}
                unitCount={unitCount}
                maxUnits={maxUnits}
                isRemoving={isRemoving}
              />
            );
          }

          // Template with defined units - show purchasedUnits when editable (shop view)
          if (editable) {
            return (
              <UnitsPreview
                purchasedUnits={purchasedUnits}
                emptyMessage={t('loadout.applying_template')}
                editable={editable}
                onRemoveUnit={onRemoveUnit}
                isRemoving={isRemoving}
              />
            );
          }

          // Non-editable: show template definition (visual_data from metadata)
          return (
            <UnitsPreview
              units={template.units}
            />
          );
        })()}

        {/* Shop button - only for human mode */}
        {mode === 'human' && onGoToShop && (
          <button
            type="button"
            className="loadout-shop-btn"
            onClick={onGoToShop}
            title={t('loadout.go_to_shop')}
          >
            <ShoppingCart size={18} />
          </button>
        )}
      </div>

      {/* Custom Templates Modal */}
      {mode === 'human' && (
        <CustomTemplatesModal
          isOpen={isCustomModalOpen}
          onClose={() => setIsCustomModalOpen(false)}
          playerTemplates={playerTemplates}
          purchasedUnits={purchasedUnits}
          onApplyTemplate={onApplyLoadout}
          onCreateTemplate={onCreateLoadout}
          onUpdateTemplate={onUpdateLoadout}
          onDeleteTemplate={onDeleteLoadout}
          isCreating={isCreatingLoadout}
          isUpdating={isUpdatingLoadout}
          isDeleting={isDeletingLoadout}
          maxTemplates={maxCustomTemplates}
        />
      )}
    </div>
  );
}

// Re-export category metadata for backward compatibility
export { CATEGORY_META };
